import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTenantSnapshot } from '@auw/onprem';
import { resolveTenantPaymentsPaddle } from '@auw/tenant';
import { resolveAppLocale } from '@auw/i18n';
import { modeToPath } from '@auw/workspace-utils';
import { activeTenantKey as tenantKey } from '@auw/tenant';

type PaddleEnvironment = 'sandbox' | 'production';

interface PaddleEvent {
  name?: string;
  data?: Record<string, unknown>;
}

interface PaddleApi {
  Initialize: (options: {
    token: string;
    checkout?: {
      settings?: Record<string, unknown>;
    };
    eventCallback?: (event: PaddleEvent) => void;
  }) => void;
  Checkout: {
    open: (options: { transactionId: string }) => void;
    close?: () => void;
  };
  Environment?: {
    set: (env: PaddleEnvironment) => void;
  };
}

interface WindowWithPaddle extends Window {
  Paddle?: PaddleApi;
  __askyouraiPaddleInitialized?: boolean;
}

const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';
const ENV_CLIENT_TOKEN = String(import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '').trim();
const ENV_RAW = String(import.meta.env.VITE_PADDLE_ENV || 'sandbox').trim().toLowerCase();
const ENV_PADDLE_ENV: PaddleEnvironment =
  ENV_RAW === 'production' || ENV_RAW === 'prod' || ENV_RAW === 'live'
    ? 'production'
    : 'sandbox';
const RECENT_TXN_STORAGE_KEY = tenantKey('paddle_recent_txn');
const RECENT_TXN_TTL_MS = 5000;
const SUCCESS_REDIRECT_DELAY_MS = 1200;
const CHECKOUT_CLOSE_DELAY_MS = 250;

/**
 * Phase 8 — tenant-aware Paddle config.
 *
 * Returns `null` when the tenant manifest sets `payments.paddle.enabled
 * !== true` (on-prem default). Returns a best-effort config otherwise,
 * falling back to `VITE_PADDLE_*` env vars so the B2C SaaS wiring
 * behaves exactly as it did pre-Phase-8.
 */
function resolvePaddleConfig(): {
  readonly clientToken: string;
  readonly environment: PaddleEnvironment;
} | null {
  const tenant = getTenantSnapshot();
  if (tenant) {
    const paddle = resolveTenantPaymentsPaddle(tenant);
    if (!paddle) return null;
    const clientToken =
      (typeof paddle.clientToken === 'string' && paddle.clientToken.trim()) ||
      ENV_CLIENT_TOKEN;
    const environment = paddle.environment ?? ENV_PADDLE_ENV;
    return { clientToken, environment };
  }
  return { clientToken: ENV_CLIENT_TOKEN, environment: ENV_PADDLE_ENV };
}

function _window(): WindowWithPaddle {
  return window as WindowWithPaddle;
}

function loadPaddleScript(): Promise<void> {
  if (!resolvePaddleConfig()) {
    return Promise.reject(new Error('PADDLE_DISABLED_FOR_TENANT'));
  }
  if (_window().Paddle) return Promise.resolve();

  return new Promise((resolve, reject) => {
    let script = document.querySelector('script[data-askyourai-paddle="1"]') as HTMLScriptElement | null;
    const handleLoad = () => resolve();
    const handleError = () => reject(new Error('PADDLE_SCRIPT_LOAD_FAILED'));

    if (!script) {
      script = document.createElement('script');
      script.src = PADDLE_JS_URL;
      script.async = true;
      script.setAttribute('data-askyourai-paddle', '1');
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      document.head.appendChild(script);
      return;
    }

    if (_window().Paddle) {
      resolve();
      return;
    }

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
  });
}

function recentlyOpenedTxnId(): string {
  try {
    const raw = sessionStorage.getItem(RECENT_TXN_STORAGE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { id?: string; ts?: number };
    if (!parsed?.id || typeof parsed.ts !== 'number') return '';
    if (Date.now() - parsed.ts > RECENT_TXN_TTL_MS) return '';
    return parsed.id;
  } catch {
    return '';
  }
}

function markRecentlyOpenedTxn(txnId: string): void {
  try {
    sessionStorage.setItem(
      RECENT_TXN_STORAGE_KEY,
      JSON.stringify({ id: txnId, ts: Date.now() }),
    );
  } catch {
    // ignore storage write failures
  }
}

function getPaddleLocale(language: string): string {
  const appLocale = resolveAppLocale(language);
  switch (appLocale) {
    case 'ko':
      return 'ko';
    case 'ja':
      return 'ja';
    case 'zh-CN':
    case 'zh-TW':
      return 'zh';
    default:
      return 'en';
  }
}

export const PaddleCheckoutPage: React.FC = () => {
  const { t, i18n } = useTranslation('widget');
  const nav = useNavigate();
  const txnId = useMemo(() => {
    const query = new URLSearchParams(window.location.search);
    return String(query.get('_ptxn') || '').trim();
  }, []);
  const paddleLocale = useMemo(() => getPaddleLocale(i18n.language), [i18n.language]);

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [message, setMessage] = useState(t('checkout.preparing'));

  const openCheckout = useCallback(
    async (force = false) => {
      try {
        setStatus('loading');
        setMessage(t('checkout.preparing'));

        if (!txnId) {
          throw new Error(t('checkout.missingTxnParam'));
        }
        const paddleConfig = resolvePaddleConfig();
        if (!paddleConfig) {
          throw new Error('PADDLE_DISABLED_FOR_TENANT');
        }
        if (!paddleConfig.clientToken) {
          throw new Error(t('checkout.missingClientToken'));
        }
        if (!force && recentlyOpenedTxnId() === txnId) {
          setStatus('ready');
          setMessage(t('checkout.closedOrBlocked'));
          return;
        }

        await loadPaddleScript();
        const paddle = _window().Paddle;
        if (!paddle) {
          throw new Error(t('checkout.sdkInitFailed'));
        }

        if (!_window().__askyouraiPaddleInitialized) {
          if (paddleConfig.environment === 'sandbox' && paddle.Environment?.set) {
            paddle.Environment.set('sandbox');
          }
          paddle.Initialize({
            token: paddleConfig.clientToken,
            checkout: {
              settings: {
                displayMode: 'overlay',
                locale: paddleLocale,
              },
            },
            eventCallback: (event: PaddleEvent) => {
              const eventName = String(event?.name || '').trim();
              if (eventName === 'checkout.closed') {
                setStatus('ready');
                setMessage(t('checkout.checkoutClosed'));
                return;
              }
              if (eventName !== 'checkout.completed') return;

              setStatus('ready');
              setMessage(t('checkout.paymentCompleted'));
              // 일부 환경에서 성공 후 오버레이가 남는 경우가 있어 명시적으로 닫습니다.
              if (typeof paddle.Checkout.close === 'function') {
                window.setTimeout(() => {
                  try {
                    paddle.Checkout.close?.();
                  } catch {
                    // ignore close failures; redirect below still recovers UX
                  }
                }, CHECKOUT_CLOSE_DELAY_MS);
              }
              window.setTimeout(() => {
                // SPA navigate만으로는 Paddle overlay가 남을 수 있어 페이지 이동으로 확정 종료.
                // Phase 11-3a: tenant 의 agent 랜딩으로 (B2C 는 `/agents`, on-prem 은
                // tenant.workspace.defaultPath 로 자동 fallback).
                window.location.assign(modeToPath('agent'));
              }, SUCCESS_REDIRECT_DELAY_MS);
            },
          });
          _window().__askyouraiPaddleInitialized = true;
        }

        markRecentlyOpenedTxn(txnId);
        paddle.Checkout.open({ transactionId: txnId });
        setStatus('ready');
        setMessage(t('checkout.checkoutOpened'));
      } catch (err) {
        const raw = err instanceof Error ? err.message : '';
        const detail = raw === 'PADDLE_SCRIPT_LOAD_FAILED' ? t('checkout.paddleScriptFailed') : (raw || t('checkout.failedToOpen'));
        setStatus('error');
        setMessage(detail);
      }
    },
    [paddleLocale, txnId, t],
  );

  useEffect(() => {
    void openCheckout(false);
  }, [openCheckout]);

  return (
    <Wrap>
      <Card>
        <Title>{t('checkout.title')}</Title>
        <Description>{message}</Description>
        <ButtonRow>
          <ActionButton onClick={() => { void openCheckout(true); }}>
            {t('checkout.reopenCheckout')}
          </ActionButton>
          <GhostButton onClick={() => { nav('/pricing'); }}>
            {t('checkout.backToPricing')}
          </GhostButton>
        </ButtonRow>
        {status === 'error' && (
          <Hint>
            {t('checkout.checkRedirectHint')}
          </Hint>
        )}
      </Card>
    </Wrap>
  );
};

export default PaddleCheckoutPage;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const Card = styled.div`
  width: 100%;
  max-width: 560px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgPrimary};
  padding: 24px;
`;

const Title = styled.h1`
  margin: 0 0 10px;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Description = styled.p`
  margin: 0 0 16px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 10px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
`;

const GhostButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 10px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
`;

const Hint = styled.p`
  margin: 14px 0 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;
